import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "react-query";
import axios, { AxiosError } from "axios";
import McmAnnouncement from "../model/McmAnnouncement";
import CreateMcmAnnouncementRequest from "../request/CreateMcmAnnouncementRequest";
import UpdateMcmAnnouncementRequest from "../request/UpdateMcmAnnouncementRequest";
import EstimateAudienceRequest from "../request/EstimateAudienceRequest";
import EstimateAudienceResponse from "../model/EstimateAudienceResponse";
import { HttpClientError } from "../../../../providers/HttpClient";

// --- Create Announcement ---

export const CreateMcmAnnouncementApiService = async (
  request: CreateMcmAnnouncementRequest,
): Promise<McmAnnouncement> => {
  try {
    const url = "/mcm-announcements/";
    const { data } = await axios.post(url, request);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

export const useCreateMcmAnnouncementApiService = (): UseMutationResult<
  McmAnnouncement,
  Error | AxiosError,
  CreateMcmAnnouncementRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<
    McmAnnouncement,
    Error | AxiosError,
    CreateMcmAnnouncementRequest
  >(CreateMcmAnnouncementApiService, {
    mutationKey: ["CreateMcmAnnouncementApiService"],
    onSuccess: () => {
      queryClient.invalidateQueries(["McmAnnouncementListApiService"]);
    },
  });
};

// --- Update Announcement ---

export const UpdateMcmAnnouncementApiService = async (
  request: UpdateMcmAnnouncementRequest,
): Promise<McmAnnouncement> => {
  try {
    const { id, ...body } = request;
    const url = `/mcm-announcements/${id}/`;
    const { data } = await axios.patch(url, body);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

export const useUpdateMcmAnnouncementApiService = (): UseMutationResult<
  McmAnnouncement,
  Error | AxiosError,
  UpdateMcmAnnouncementRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<
    McmAnnouncement,
    Error | AxiosError,
    UpdateMcmAnnouncementRequest
  >(UpdateMcmAnnouncementApiService, {
    mutationKey: ["UpdateMcmAnnouncementApiService"],
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["McmAnnouncementListApiService"]);
      queryClient.invalidateQueries([
        "McmAnnouncementDetailApiService",
        variables.id,
      ]);
    },
  });
};

// --- Delete Announcement ---

const DeleteMcmAnnouncementApiService = async (
  id: string,
): Promise<boolean> => {
  try {
    const url = `/mcm-announcements/${id}/`;
    const { data } = await axios.delete(url);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useDeleteMcmAnnouncementApiService = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error | AxiosError, string>(
    DeleteMcmAnnouncementApiService,
    {
      mutationKey: ["DeleteMcmAnnouncementApiService"],
      onSuccess: () => {
        queryClient.invalidateQueries(["McmAnnouncementListApiService"]);
      },
    },
  );
};

// --- Publish Announcement ---

export const PublishMcmAnnouncementApiService = async (
  id: string,
): Promise<McmAnnouncement> => {
  try {
    const url = `/mcm-announcements/${id}/publish/`;
    const { data } = await axios.post(url);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

export const usePublishMcmAnnouncementApiService = (): UseMutationResult<
  McmAnnouncement,
  Error | AxiosError,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation<McmAnnouncement, Error | AxiosError, string>(
    PublishMcmAnnouncementApiService,
    {
      mutationKey: ["PublishMcmAnnouncementApiService"],
      onSuccess: (_data, id) => {
        queryClient.invalidateQueries(["McmAnnouncementListApiService"]);
        queryClient.invalidateQueries([
          "McmAnnouncementDetailApiService",
          id,
        ]);
      },
    },
  );
};

// --- Close Announcement ---

export const CloseMcmAnnouncementApiService = async (
  id: string,
): Promise<McmAnnouncement> => {
  try {
    const url = `/mcm-announcements/${id}/close/`;
    const { data } = await axios.post(url);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

export const useCloseMcmAnnouncementApiService = (): UseMutationResult<
  McmAnnouncement,
  Error | AxiosError,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation<McmAnnouncement, Error | AxiosError, string>(
    CloseMcmAnnouncementApiService,
    {
      mutationKey: ["CloseMcmAnnouncementApiService"],
      onSuccess: (_data, id) => {
        queryClient.invalidateQueries(["McmAnnouncementListApiService"]);
        queryClient.invalidateQueries([
          "McmAnnouncementDetailApiService",
          id,
        ]);
      },
    },
  );
};

// --- Estimate Audience ---

export const EstimateAudienceApiService = async (
  request: EstimateAudienceRequest,
): Promise<EstimateAudienceResponse> => {
  try {
    const url = "/mcm-announcements/estimate-audience/";
    const { data } = await axios.post(url, request);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

export const useEstimateAudienceApiService = (): UseMutationResult<
  EstimateAudienceResponse,
  Error | AxiosError,
  EstimateAudienceRequest
> =>
  useMutation<EstimateAudienceResponse, Error | AxiosError, EstimateAudienceRequest>(
    EstimateAudienceApiService,
    { mutationKey: ["EstimateAudienceApiService"] },
  );
